import type { ReportChange } from 'components/DeepSearch/components/ReportLibrary/MonitoredReports/MonitoringChangesPanel/types';

interface DateGroup {
  date: string;
  changes: ReportChange[];
}

interface SectionGroup {
  section: string;
  changes: ReportChange[];
}

const sectionLabelMap: Record<string, string> = {
  usernames: 'New usernames added',
  profiles: 'New profiles added',
  jobs: 'New jobs added',
  educations: 'New educations added',
  phones: 'New phone number added',
  emails: 'New email added',
  addresses: 'New address added',
  associates: 'New associates added',
  person_updates: 'New updates found',
  people: 'New updates found',
  relatives: 'New relatives added',
  criminal_records: 'New criminal records added',
  bankruptcies: 'New bankruptcies added',
  liens: 'New liens added',
  judgments: 'New judgments added',
  social_media: 'New social media added',
  vehicles: 'New vehicles added',
  properties: 'New properties added',
  assets: 'New assets added',
  licenses: 'New licenses added',
  possible_photos: 'New possible photos added',
};

export const getSectionLabel = (section: string): string => {
  if (sectionLabelMap[section]) {
    return sectionLabelMap[section];
  }

  const formatted = section
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return `New ${formatted.toLowerCase()} added`;
};

const toDateKey = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().split('T')[0];
};

export const groupChangesByDate = (changes: ReportChange[]): DateGroup[] => {
  const grouped = new Map<string, ReportChange[]>();

  for (const change of changes) {
    const key = toDateKey(change.created_at);

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }

    grouped.get(key)!.push(change);
  }

  return Array.from(grouped.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, changes]) => ({ date, changes }));
};

export const groupChangesBySection = (
  changes: ReportChange[]
): SectionGroup[] => {
  const grouped = new Map<string, ReportChange[]>();

  for (const change of changes) {
    const section = change.report_section;

    if (!grouped.has(section)) {
      grouped.set(section, []);
    }

    grouped.get(section)!.push(change);
  }

  return Array.from(grouped.entries()).map(([section, changes]) => ({
    section,
    changes,
  }));
};
